import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { Course, Courseversion, Lecturer, Media } from '@/payload/payload-types';
import Image from 'next/image';
import LinkedIn from '../../../_icons/LinkedIn';
import { TabsContentTitle } from '../TabsContentTitle';
import RichTextLexical from '../../RichTextLexical';
import { logToPayload } from '@/app/(frontend)/_utilities/log-to-payload';

export const Lecturers: React.FC<{
	courseversion: Courseversion;
	course: Course;
}> = props => {
	const t = useTranslations('Course');
	const { courseversion, course } = props;

	interface LecturerTypes {
		leadingLecturers: Courseversion['lecturers'];
		additionalLecturers: Courseversion['lecturers'];
	}
	const { leadingLecturers, additionalLecturers } = courseversion.lecturers.reduce<LecturerTypes>(
		(collection, _lecturer) => {
			if (_lecturer.isLeading) {
				collection.leadingLecturers.push(_lecturer);

				return collection;
			} else {
				collection.additionalLecturers.push(_lecturer);

				return collection;
			}
		},
		{
			leadingLecturers: [],
			additionalLecturers: [],
		},
	);

	return (
		<Fragment>
			<TabsContentTitle
				title={t('lecturer-page-title', { count: leadingLecturers.length })}
			/>
			<div className="flex flex-col space-y-20">
				<div className="flex flex-col space-y-16">
					{leadingLecturers.map(leadingLecturer => {
						if (typeof leadingLecturer.lecturer === 'string') {
							logToPayload('LecturersTab: Bad depth for lecturer ', {
								leadingLecturer: leadingLecturer.lecturer,
								courseId: course.id,
								courseversionId: courseversion.id,
							});

							return null;
						}

						const lecturer = leadingLecturer.lecturer;
						const lecturerPicture = lecturer.picture;

						if (typeof lecturerPicture === 'string') {
							logToPayload('Lecturesrs: bad depth for lecturerPicture', {
								lecturerId: lecturer.id,
								courseId: course.id,
							});
							return null;
						}

						const lecturerImage =
							lecturerPicture?.sizes?.avatar?.url || lecturerPicture?.url;

						if (!lecturerImage) {
							logToPayload('LecturersTab: lecturerImage missing ', {
								lecturerId: lecturer.id,
								lecturerImage,
								courseId: course.id,
								courseversionId: courseversion.id,
							});
						}

						if (typeof lecturerPicture === 'string') {
							logToPayload('LecturersTab: Bad depth for lecturer: ', {
								lecturer: leadingLecturer.lecturer?.id,
								courseID: course.id,
								courseversionId: courseversion.id,
							});
							return null;
						}
						return (
							<div key={`leadingLecturer-${leadingLecturer.id}`}>
								<div className="mb-12 flex items-center">
									{lecturerImage && (
										<Image
											alt={lecturer.name}
											className="mr-8 rounded-full"
											height={120}
											quality={100}
											src={lecturerImage}
											width={120}
											style={{ objectFit: 'cover', height: '120px' }}
										/>
									)}
									<div className="flex flex-col">
										<p className="text-muted-navy font-bold">{lecturer.name}</p>
										<p className="text-gold-light text-sm">
											{lecturer.title
												? t(lecturer.title)
												: lecturer.customLecturerTitle || ''}
										</p>
									</div>
								</div>
								{lecturer.bioRichTextLexical && (
									<RichTextLexical content={lecturer.bioRichTextLexical} />
								)}
							</div>
						);
					})}
				</div>

				{Boolean(additionalLecturers?.length) && (
					<div>
						<div className="space-y-8">
							{additionalLecturers.map((additionalLecturer, i) => {
								const lecturer = additionalLecturer.lecturer as Lecturer;

								return (
									<section key={`additional-lecturer-${lecturer.name || i}`}>
										<p className="text-muted-navy flex items-center font-bold">
											{lecturer.name}
											{lecturer.linkedin && (
												<a
													href={lecturer.linkedin}
													className="ml-3"
													target="_blank"
												>
													<LinkedIn></LinkedIn>
												</a>
											)}
										</p>
										<p className="text-gold-light m-0 text-sm">
											{lecturer.title
												? t(lecturer.title)
												: lecturer.customLecturerTitle || ''}
										</p>
									</section>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</Fragment>
	);
};
